import axios from 'axios';

let isRefreshing = false;
let failedQueue = [];
let appRouter = null;
let appStore = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Initialize function to set router and store references
export function initializeInterceptor(router, store) {
  appRouter = router;
  appStore = store;
}

// Request interceptor - Add token to all requests
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('user-token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors and refresh token
axios.interceptors.response.use(
  response => {
    return response;
  },
  async error => {
    const originalRequest = error.config;

    // If error is not 401 or is from login/refresh endpoints, reject immediately
    if (
      error.response?.status !== 401 ||
      originalRequest.url?.includes('/user/login') ||
      originalRequest.url?.includes('/user/refresh') ||
      originalRequest._retry
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return axios(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refresh-token');
    const rememberMe = localStorage.getItem('remember-me') === 'true';

    // Si no hay refresh token O el usuario no marcó "recordarme", hacer logout
    if (!refreshToken || !rememberMe) {
      isRefreshing = false;

      // Logout via store if available
      if (appStore) {
        appStore.dispatch('api/logout');
      } else {
        localStorage.removeItem('user-token');
        localStorage.removeItem('refresh-token');
        localStorage.removeItem('remember-me');
        localStorage.removeItem('login-info');
      }

      // Redirect to login if router available
      if (appRouter && appRouter.currentRoute.path !== '/') {
        appRouter.push('/');
      }

      return Promise.reject(error);
    }

    try {
      // Attempt to refresh the token
      const response = await axios.post('/user/refresh', {
        refresh_token: refreshToken
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;

      // Update tokens in localStorage and axios
      localStorage.setItem('user-token', access_token);
      localStorage.setItem('refresh-token', newRefreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Update Vuex store if available
      if (appStore) {
        appStore.commit('api/AUTH_SUCCESS', access_token);
        appStore.commit('api/SET_REFRESH_TOKEN', newRefreshToken);

        // Update login info with new user data if available
        if (response.data.user) {
          const loginInfo = JSON.parse(localStorage.getItem('login-info') || '{}');
          loginInfo.user = response.data.user;
          localStorage.setItem('login-info', JSON.stringify(loginInfo));
        }
      }

      // Process the queued requests with the new token
      processQueue(null, access_token);

      // Retry the original request with new token
      originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
      return axios(originalRequest);
    } catch (refreshError) {
      // Refresh failed, logout and redirect to login
      processQueue(refreshError, null);

      // Logout via store if available
      if (appStore) {
        appStore.dispatch('api/logout');
      } else {
        localStorage.removeItem('user-token');
        localStorage.removeItem('refresh-token');
        localStorage.removeItem('remember-me');
        localStorage.removeItem('login-info');
      }

      // Redirect to login if router available
      if (appRouter && appRouter.currentRoute.path !== '/') {
        appRouter.push('/');
      }

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axios;
